import type { Basis } from '../types/charge';

export type FormulaCharge = {
  id: string;
  calcMethod: 'flat' | 'percentage';
  rateBps: number | null;
  basis: Basis | null;
  amount: number;
};

export type LineItemLike = {
  unitCost: number;
  quantity: number | null;
  sectionNormalized: string | null;
};

export type Package = {
  lineItems: LineItemLike[];
  charges: FormulaCharge[];
  selectedAddOns: LineItemLike[];
};

export type ChargeError =
  | { kind: 'cycle'; chargeIds: string[] }
  | { kind: 'dangling-charge-ref'; chargeId: string; missingId: string }
  | { kind: 'zero-basis'; chargeId: string; basis: Basis };

export type ComputeResult = {
  lineItemTotal: number;
  addOnTotal: number;
  chargesById: Record<string, number>;
  packageTotal: number;
  errors: ChargeError[];
};

/**
 * Public helper: compute the total dollar value of a basis given a package's
 * line items, selected add-ons, and a map of resolved sibling charge amounts.
 *
 * Used by the quote-import pipeline to verify LLM-supplied formulas and by the
 * back-solver caller to confirm the basis still makes sense at insert time.
 *
 * Unlike `computePackage`, this does not detect cycles or recompute siblings —
 * it expects callers to pass already-known sibling amounts via `siblingAmounts`.
 * Unknown sibling references contribute 0.
 */
export function resolveBasisTotal(
  basis: Basis,
  pkg: {
    lineItems: LineItemLike[];
    selectedAddOns: LineItemLike[];
    siblingAmounts?: Map<string, number> | Record<string, number>;
  },
): number {
  const lineItemTotal = sumLineItems(pkg.lineItems);
  const addOnTotal = sumLineItems(pkg.selectedAddOns);
  const sectionTotals = bucketSectionTotals([...pkg.lineItems, ...pkg.selectedAddOns]);
  const siblingMap: Map<string, number> =
    pkg.siblingAmounts instanceof Map
      ? pkg.siblingAmounts
      : new Map(Object.entries(pkg.siblingAmounts ?? {}));

  function walk(b: Basis): number {
    if (b.kind === 'package_subtotal') return lineItemTotal + addOnTotal;
    if (b.kind === 'sections') {
      let sum = 0;
      for (const section of b.sections) sum += sectionTotals.get(section) ?? 0;
      return sum;
    }
    if (b.kind === 'charges') {
      let sum = 0;
      for (const id of b.chargeIds) sum += siblingMap.get(id) ?? 0;
      return sum;
    }
    if (b.kind === 'composite') {
      let sum = 0;
      for (const part of b.parts) sum += walk(part);
      return sum;
    }
    b satisfies never;
    return 0;
  }

  return walk(basis);
}

export function bankersRound(value: number): number {
  const sign = value < 0 ? -1 : 1;
  const abs = Math.abs(value);
  const truncated = Math.trunc(abs);
  const frac = abs - truncated;
  let rounded: number;
  if (Math.abs(frac - 0.5) < 1e-9) {
    rounded = truncated % 2 === 0 ? truncated : truncated + 1;
  } else {
    rounded = Math.round(abs);
  }
  const result = sign * rounded;
  return result === 0 ? 0 : result; // normalize -0 to 0
}

function sumLineItems(items: LineItemLike[]): number {
  let total = 0;
  for (const item of items) {
    const qty = item.quantity == null ? 1 : item.quantity;
    total += item.unitCost * qty;
  }
  return total;
}

function bucketSectionTotals(items: LineItemLike[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const item of items) {
    if (item.sectionNormalized == null) continue;
    const qty = item.quantity == null ? 1 : item.quantity;
    const value = item.unitCost * qty;
    map.set(item.sectionNormalized, (map.get(item.sectionNormalized) ?? 0) + value);
  }
  return map;
}

function extractChargeRefs(basis: Basis | null): string[] {
  if (basis == null) return [];
  if (basis.kind === 'charges') return basis.chargeIds;
  if (basis.kind === 'composite') {
    const out: string[] = [];
    for (const part of basis.parts) out.push(...extractChargeRefs(part));
    return out;
  }
  return [];
}

type TopoResult = { order: FormulaCharge[]; cycleMembers: Set<string> };

function topoSortCharges(charges: FormulaCharge[]): TopoResult {
  const byId = new Map<string, FormulaCharge>();
  for (const c of charges) byId.set(c.id, c);

  // Build edges: charge -> list of charge ids it depends on (only those present in byId)
  const deps = new Map<string, string[]>();
  for (const c of charges) {
    if (c.calcMethod !== 'percentage') {
      deps.set(c.id, []);
      continue;
    }
    const refs = extractChargeRefs(c.basis).filter((id) => byId.has(id));
    deps.set(c.id, refs);
  }

  // DFS with onStack tracking to detect cycles
  const order: FormulaCharge[] = [];
  const visited = new Set<string>();
  const onStack = new Set<string>();
  const cycleMembers = new Set<string>();

  function visit(id: string, path: string[]): void {
    if (cycleMembers.has(id)) return;
    if (visited.has(id)) return;
    if (onStack.has(id)) {
      // Found cycle: members are everything from where `id` first appears in path
      const startIdx = path.indexOf(id);
      const members = path.slice(startIdx);
      for (const m of members) cycleMembers.add(m);
      cycleMembers.add(id);
      return;
    }
    onStack.add(id);
    path.push(id);
    const dependencies = deps.get(id) ?? [];
    for (const dep of dependencies) {
      visit(dep, path);
    }
    path.pop();
    onStack.delete(id);
    if (!cycleMembers.has(id)) {
      visited.add(id);
      const charge = byId.get(id);
      if (charge) order.push(charge);
    }
  }

  for (const c of charges) {
    visit(c.id, []);
  }

  return { order, cycleMembers };
}

type ResolveCtx = {
  lineItemTotal: number;
  addOnTotal: number;
  sectionTotals: Map<string, number>;
  resolvedAmounts: Map<string, number>;
  knownChargeIds: Set<string>;
  chargeId: string;
};

function resolveBasis(
  basis: Basis,
  ctx: ResolveCtx,
): { total: number; danglingErrors: ChargeError[] } {
  const danglingErrors: ChargeError[] = [];
  const total = walk(basis);
  return { total, danglingErrors };

  function walk(b: Basis): number {
    if (b.kind === 'package_subtotal') {
      return ctx.lineItemTotal + ctx.addOnTotal;
    }
    if (b.kind === 'sections') {
      let sum = 0;
      for (const section of b.sections) {
        sum += ctx.sectionTotals.get(section) ?? 0;
      }
      return sum;
    }
    if (b.kind === 'charges') {
      let sum = 0;
      for (const id of b.chargeIds) {
        if (!ctx.knownChargeIds.has(id)) {
          danglingErrors.push({ kind: 'dangling-charge-ref', chargeId: ctx.chargeId, missingId: id });
          continue;
        }
        sum += ctx.resolvedAmounts.get(id) ?? 0;
      }
      return sum;
    }
    if (b.kind === 'composite') {
      let sum = 0;
      for (const part of b.parts) sum += walk(part);
      return sum;
    }
    b satisfies never;
    return 0;
  }
}

export function computePackage(pkg: Package): ComputeResult {
  const lineItemTotal = sumLineItems(pkg.lineItems);
  const addOnTotal = sumLineItems(pkg.selectedAddOns);

  // Section totals draw from BOTH line items and add-ons
  const sectionTotals = bucketSectionTotals([...pkg.lineItems, ...pkg.selectedAddOns]);

  const knownChargeIds = new Set(pkg.charges.map((c) => c.id));
  const { order, cycleMembers } = topoSortCharges(pkg.charges);

  const resolvedAmounts = new Map<string, number>();
  const errors: ChargeError[] = [];

  if (cycleMembers.size > 0) {
    errors.push({ kind: 'cycle', chargeIds: Array.from(cycleMembers) });
    for (const c of pkg.charges) {
      if (cycleMembers.has(c.id)) resolvedAmounts.set(c.id, c.amount);
    }
  }

  for (const charge of order) {
    if (resolvedAmounts.has(charge.id)) continue;

    if (charge.calcMethod === 'flat' || charge.basis == null || charge.rateBps == null) {
      resolvedAmounts.set(charge.id, charge.amount);
      continue;
    }

    const ctx: ResolveCtx = {
      lineItemTotal,
      addOnTotal,
      sectionTotals,
      resolvedAmounts,
      knownChargeIds,
      chargeId: charge.id,
    };
    const { total: basisTotal, danglingErrors } = resolveBasis(charge.basis, ctx);
    for (const e of danglingErrors) errors.push(e);

    if (basisTotal === 0) {
      errors.push({ kind: 'zero-basis', chargeId: charge.id, basis: charge.basis });
      resolvedAmounts.set(charge.id, charge.amount);
      continue;
    }

    // Multiply first then divide to minimize fp error
    const computed = bankersRound((charge.rateBps * basisTotal) / 10000);
    resolvedAmounts.set(charge.id, computed);
  }

  // Build chargesById preserving original input order for stable output
  const chargesById: Record<string, number> = {};
  let chargesSum = 0;
  for (const c of pkg.charges) {
    const v = resolvedAmounts.get(c.id) ?? c.amount;
    chargesById[c.id] = v;
    chargesSum += v;
  }

  return {
    lineItemTotal,
    addOnTotal,
    chargesById,
    packageTotal: lineItemTotal + addOnTotal + chargesSum,
    errors,
  };
}
